import { useState, useEffect, useRef } from 'react';
import { 
  Play, X, Check, ChevronLeft, ChevronRight, Bookmark, BookmarkCheck,
  Clock, Zap, Filter, List, AlertCircle, Trophy, Target, XCircle, Flag, Eye
} from 'lucide-react';
import axios from 'axios';
// import { useTranslation } from 'react-i18next'; // DISABLED FOR NOW
import ErrorReportButton from './ErrorReportButton';
import ImageLightbox from './ImageLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to fix image URLs - handles old /uploads/ and new /api/uploads/ formats
// Also handles old domain URLs that need to be redirected to current domain
const fixImageUrl = (url) => {
  if (!url) return null;
  
  // If already a full http URL
  if (url.startsWith('http')) {
    // If URL is from Cloudflare R2 (images.ehliyet.nu), return as-is
    if (url.includes('images.ehliyet.nu') || url.includes('.r2.dev')) {
      return url;
    }
    
    // List of old domains that need to be redirected
    const oldDomains = [
      'swedriving.preview.emergentagent.com',
      'teoriprov-3.preview.emergentagent.com',
      'teoriprov-2.preview.emergentagent.com',
      'teoriprov.preview.emergentagent.com',
      'drivingtheory-1.preview.emergentagent.com',
      'drivingtheory.preview.emergentagent.com',
      'turkswedtheory.preview.emergentagent.com',
      'svenskteori.preview.emergentagent.com',
      'ehliyet.preview.emergentagent.com',
      'swedish-driver-test.preview.emergentagent.com',
      'swedish-theory-test-1.preview.emergentagent.com',
      'swedish-theory-test.preview.emergentagent.com',
      'testprep-tr.preview.emergentagent.com',
      'testprep-tr.emergent.host',
      'testtheory.preview.emergentagent.com'
    ];
    
    // Check if URL is from an old domain
    for (const oldDomain of oldDomains) {
      if (url.includes(oldDomain)) {
        // Extract the path after the domain
        try {
          const urlObj = new URL(url);
          const path = urlObj.pathname;
          // Ensure path has /api prefix
          const fixedPath = path.startsWith('/api') ? path : `/api${path}`;
          return `${BACKEND_URL}${fixedPath}`;
        } catch (e) {
          // Fallback: extract filename
          const filename = url.split('/').pop();
          return `${BACKEND_URL}/api/uploads/${filename}`;
        }
      }
    }
    
    // Fix old format URLs that don't have /api prefix
    if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
      return url.replace('/uploads/', '/api/uploads/');
    }
    return url;
  }
  
  // Relative URL - ensure /api prefix
  if (url.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${url}`;
  }
  if (url.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}${url}`;
  }
  return `${BACKEND_URL}/api/uploads/${url}`;
};

export default function PremiumQuizMode({ onBack, user, onQuizComplete }) {
  // const { t } = useTranslation(); // DISABLED FOR NOW
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [markedQuestions, setMarkedQuestions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [timedMode, setTimedMode] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [showQuestionList, setShowQuestionList] = useState(false);
  const [answeredCurrentQuestion, setAnsweredCurrentQuestion] = useState(false);
  const [questionCount, setQuestionCount] = useState(10);
  const [customCount, setCustomCount] = useState('');
  const [showAnswersImmediately, setShowAnswersImmediately] = useState(true); // Show answer right after selection
  const [showFinishConfirm, setShowFinishConfirm] = useState(false); // Finish confirmation modal
  
  // Ref for auto-scrolling to question on navigation
  const questionContainerRef = useRef(null);

  // Auto-scroll to question when currentIndex changes
  useEffect(() => {
    if (quizStarted && questionContainerRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        questionContainerRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start'
        });
      }, 100);
    }
  }, [currentIndex, quizStarted]);

  useEffect(() => {
    fetchCategories();
  }, []);

  // Timer effect - only depends on timedMode and quizStarted
  // timeRemaining is NOT in deps to prevent interval recreation every second
  useEffect(() => {
    let timer = null;
    
    if (timedMode && quizStarted) {
      timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            // Use setTimeout to avoid state update during render
            setTimeout(() => handleFinishQuiz(), 0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [timedMode, quizStarted]);

  const fetchCategories = async () => {
    try {
      console.log('📚 Fetching categories from:', `${API}/categories`);
      const response = await axios.get(`${API}/categories`);
      console.log('✅ Categories received:', response.data);
      setCategories(response.data || []);
    } catch (error) {
      console.error('❌ Kategoriler yüklenemedi:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async () => {
    setLoading(true);
    console.log('🎯 Premium Quiz: Starting quiz...');
    try {
      // Use selected question count
      const finalCount = customCount ? parseInt(customCount) : questionCount;
      let url = `${API}/questions?limit=${finalCount}`;
      const params = [];
      
      if (selectedCategory !== 'all') {
        params.push(`category_id=${selectedCategory}`);
      }
      
      if (params.length > 0) {
        url += '&' + params.join('&');
      }

      console.log('📡 Fetching questions from:', url);
      const response = await axios.get(url);
      console.log('✅ Questions fetched:', response.data.length);
      let fetchedQuestions = response.data;
      
      // Filter by difficulty if needed
      if (selectedDifficulty !== 'all') {
        fetchedQuestions = fetchedQuestions.filter(q => q.difficulty === selectedDifficulty);
        console.log('🔍 Filtered by difficulty:', fetchedQuestions.length);
      }
      
      // Limit to selected count
      fetchedQuestions = fetchedQuestions.slice(0, finalCount);
      
      setQuestions(fetchedQuestions);
      setQuizStarted(true);
      console.log('🚀 Quiz started with', fetchedQuestions.length, 'questions');
      
      // Set time if timed mode - only if user hasn't set a custom time
      if (timedMode && timeRemaining === 0) {
        // Default: 1 minute per question
        setTimeRemaining(fetchedQuestions.length * 60);
        console.log('⏱️ Timer set to default:', fetchedQuestions.length, 'minutes');
      } else if (timedMode) {
        console.log('⏱️ Timer using custom time:', timeRemaining / 60, 'minutes');
      }
    } catch (error) {
      console.error('❌ Sorular yüklenemedi:', error);
      alert('Sorular yüklenirken bir hata oluştu: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
      console.log('🏁 Loading finished');
    }
  };

  const handleAnswer = (answer) => {
    if (answeredCurrentQuestion) return; // Prevent changing answer after seeing result
    
    setUserAnswers({
      ...userAnswers,
      [questions[currentIndex].id]: answer
    });
    setAnsweredCurrentQuestion(true);
  };

  const toggleMarkQuestion = () => {
    const questionId = questions[currentIndex].id;
    const newMarked = new Set(markedQuestions);
    
    if (newMarked.has(questionId)) {
      newMarked.delete(questionId);
    } else {
      newMarked.add(questionId);
    }
    
    setMarkedQuestions(newMarked);
  };

  const goToQuestion = (index) => {
    setCurrentIndex(index);
    setShowQuestionList(false);
    // Check if this question was already answered
    const questionId = questions[index].id;
    setAnsweredCurrentQuestion(!!userAnswers[questionId]);
  };
  
  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      const nextIdx = currentIndex + 1;
      setCurrentIndex(nextIdx);
      const nextQuestionId = questions[nextIdx].id;
      // Only set as answered if there's actually an answer
      setAnsweredCurrentQuestion(!!userAnswers[nextQuestionId]);
    }
  };
  
  const prevQuestion = () => {
    if (currentIndex > 0) {
      const prevIdx = currentIndex - 1;
      setCurrentIndex(prevIdx);
      const prevQuestionId = questions[prevIdx].id;
      // Only set as answered if there's actually an answer
      setAnsweredCurrentQuestion(!!userAnswers[prevQuestionId]);
    }
  };

  const handleFinishQuiz = async () => {
    setShowResult(true);
    
    // Calculate and save results
    const results = calculateResults();
    const questionsAnswered = questions.map(q => ({
      question_id: q.id,
      category_id: q.category_id,
      user_answer: userAnswers[q.id] || null,
      is_correct: userAnswers[q.id] === q.correct_answer
    }));
    
    // Save to backend
    try {
      await axios.post(`${API}/exam/results`, {
        user_id: user?.id || user?.user_id,
        user_email: user?.email,
        exam_type: timedMode ? 'exam' : 'practice',
        is_timed: timedMode,
        time_taken_seconds: timedMode ? (questions.length * 60 - timeRemaining) : null,
        total_questions: questions.length,
        correct_answers: results.correct,
        wrong_answers: results.wrong,
        score_percentage: results.percentage,
        questions_answered: questionsAnswered
      });
      console.log('✅ Quiz results saved successfully');
    } catch (error) {
      console.error('❌ Failed to save quiz results:', error);
    }
    
    // Notify parent component that quiz is complete
    if (onQuizComplete) {
      setTimeout(() => onQuizComplete(), 500);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateResults = () => {
    let correct = 0;
    let wrong = 0;
    let unanswered = 0;

    questions.forEach(q => {
      const answer = userAnswers[q.id];
      if (!answer) {
        unanswered++;
      } else if (answer === q.correct_answer) {
        correct++;
      } else {
        wrong++;
      }
    });

    // Calculate based on answered questions only
    const totalAnswered = correct + wrong;
    const percentage = totalAnswered > 0 ? Math.round((correct / totalAnswered) * 100) : 0;
    const passed = percentage >= 74;

    return { correct, wrong, unanswered, percentage, passed };
  };

  // Configuration Screen
  if (!quizStarted) {
    return (
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-red-950 via-red-800 to-red-900">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-blob top-0 left-0"></div>
          <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl animate-blob animation-delay-2000 top-0 right-0"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 py-12">
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-semibold">Geri Dön</span>
          </button>

          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8">
            <h1 className="text-4xl font-black text-white mb-6 flex items-center gap-3">
              <Trophy className="w-10 h-10 text-yellow-400" />
              Premium Quiz Modu
            </h1>

            <div className="space-y-6">
              {/* Category Selection */}
              <div>
                <label className="block text-white font-bold mb-3 flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Kategori Seçin
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="all">Tüm Kategoriler (2000 Soru)</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name_turkish}
                    </option>
                  ))}
                </select>
              </div>

              {/* Difficulty Selection */}
              <div>
                <label className="block text-white font-bold mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Zorluk Seviyesi
                </label>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="all">Tüm Seviyeler</option>
                  <option value="easy">Kolay</option>
                  <option value="medium">Orta</option>
                  <option value="hard">Zor</option>
                </select>
              </div>

              {/* Question Count Selection */}
              <div>
                <label className="block text-white font-bold mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Kaç Soru Çözmek İstiyorsunuz?
                </label>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {[5, 10, 50, 70].map(count => (
                    <button
                      key={count}
                      onClick={() => {
                        setQuestionCount(count);
                        setCustomCount('');
                      }}
                      className={`py-3 px-4 rounded-xl font-bold transition-all ${
                        questionCount === count && !customCount
                          ? 'bg-yellow-500 text-gray-900'
                          : 'bg-slate-800 text-white hover:bg-slate-700'
                      }`}
                    >
                      {count}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  value={customCount}
                  onChange={(e) => {
                    setCustomCount(e.target.value);
                    setQuestionCount(0);
                  }}
                  placeholder="Veya özel sayı girin (örn: 25)"
                  className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  min="1"
                  max="2000"
                />
              </div>

              {/* Timed Mode Toggle */}
              <div className="bg-white/10 rounded-xl p-6 border border-white/20 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="w-6 h-6 text-blue-400" />
                    <div>
                      <h3 className="text-white font-bold">Zamanlı Mod</h3>
                      <p className="text-white/70 text-sm">Özel süre belirleyin</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setTimedMode(!timedMode)}
                    className={`w-16 h-8 rounded-full transition-all ${
                      timedMode ? 'bg-green-500' : 'bg-white/20'
                    }`}
                  >
                    <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                      timedMode ? 'translate-x-9' : 'translate-x-1'
                    }`}></div>
                  </button>
                </div>
                
                {timedMode && (
                  <div>
                    <label className="block text-white/80 text-sm mb-2">Süre (Dakika)</label>
                    <input
                      type="number"
                      value={timeRemaining / 60 || ''}
                      onChange={(e) => setTimeRemaining(parseInt(e.target.value || 0) * 60)}
                      placeholder="Örn: 20, 50, 80"
                      className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="1"
                      max="200"
                    />
                    <p className="text-white/50 text-xs mt-2">
                      💡 20 soru için 20 dakika, 100 soru için 80 dakika önerilir
                    </p>
                  </div>
                )}
              </div>

              {/* Show Answers Immediately Toggle */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="flex items-center gap-3">
                  <Eye className="w-6 h-6 text-green-400" />
                  <div>
                    <span className="text-white font-bold">Cevapları Anında Göster</span>
                    <p className="text-white/50 text-sm">Her sorudan sonra doğru cevabı göster</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAnswersImmediately(!showAnswersImmediately)}
                  className={`w-16 h-8 rounded-full transition-all ${
                    showAnswersImmediately ? 'bg-green-500' : 'bg-white/20'
                  }`}
                >
                  <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                    showAnswersImmediately ? 'translate-x-9' : 'translate-x-1'
                  }`}></div>
                </button>
              </div>

              {/* Start Button */}
              <button
                onClick={startQuiz}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 rounded-xl font-black text-xl transition-all shadow-2xl flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <div className="w-6 h-6 border-4 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <Play className="w-6 h-6" />
                    Quize Başla
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (showResult) {
    const results = calculateResults();
    
    return (
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-red-950 via-red-800 to-red-900 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 text-center">
            <div className={`w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center ${
              results.passed ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}>
              {results.passed ? (
                <Trophy className="w-16 h-16 text-green-400" />
              ) : (
                <AlertCircle className="w-16 h-16 text-red-400" />
              )}
            </div>

            <h1 className={`text-5xl font-black mb-4 ${
              results.passed ? 'text-green-400' : 'text-red-400'
            }`}>
              {results.passed ? '🎉 Tebrikler!' : '😔 Tekrar Deneyin'}
            </h1>

            <p className="text-white text-xl mb-8">
              {results.passed 
                ? 'Quizi başarıyla tamamladınız!' 
                : 'Geçmek için %74 ve üzeri puan gerekiyor.'}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-white/10 rounded-xl p-6">
                <div className="text-5xl font-black text-white mb-2">{results.percentage}%</div>
                <div className="text-white/70">Başarı Oranı</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <div className="text-5xl font-black text-white mb-2">{results.correct + results.wrong}</div>
                <div className="text-white/70">Toplam Soru</div>
              </div>
              <div className="bg-green-500/20 rounded-xl p-6">
                <div className="text-4xl font-black text-green-400 mb-2">{results.correct}</div>
                <div className="text-white/70">Doğru</div>
              </div>
              <div className="bg-red-500/20 rounded-xl p-6">
                <div className="text-4xl font-black text-red-400 mb-2">{results.wrong}</div>
                <div className="text-white/70">Yanlış</div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowResult(false);
                  setQuizStarted(false);
                  setUserAnswers({});
                  setMarkedQuestions(new Set());
                  setCurrentIndex(0);
                }}
                className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-xl font-bold transition-all"
              >
                Yeni Quiz
              </button>
              <button
                onClick={onBack}
                className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition-all"
              >
                Ana Sayfa
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quiz Screen
  const currentQuestion = questions[currentIndex];
  if (!currentQuestion) return null;

  const isMarked = markedQuestions.has(currentQuestion.id);
  const answered = userAnswers[currentQuestion.id];
  const totalAnswered = Object.keys(userAnswers).length;
  const isCorrect = answered === currentQuestion.correct_answer;
  const showFeedback = answeredCurrentQuestion && answered;

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-red-950 via-red-800 to-red-900">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-blob top-0 left-0"></div>
        <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl animate-blob animation-delay-2000 top-0 right-0"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="bg-red-600 text-white px-6 py-3 rounded-full font-bold text-lg flex items-center gap-2">
              <Play className="w-5 h-5" />
              Premium Quiz - Soru {currentIndex + 1} / {questions.length}
            </div>
            
            {timedMode && (
              <div className={`px-6 py-3 rounded-full font-bold text-lg flex items-center gap-2 ${
                timeRemaining < 300 ? 'bg-red-500 animate-pulse' : 'bg-blue-500'
              } text-white`}>
                <Clock className="w-5 h-5" />
                {formatTime(timeRemaining)}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (window.confirm('Quiz\'ten çıkmak istediğinize emin misiniz? İlerlemeniz kaydedilecek.')) {
                  onBack();
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-bold transition-all"
            >
              <X className="w-5 h-5" />
              Kapat
            </button>
            
            <button
              onClick={() => setShowQuestionList(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition-all"
            >
              <Trophy className="w-5 h-5" />
              İstatistik
            </button>
            
            <button
              onClick={() => setShowQuestionList(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all"
            >
              <List className="w-5 h-5" />
              Genel Bakış
            </button>
            
            <button
              onClick={toggleMarkQuestion}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all ${
                isMarked 
                  ? 'bg-yellow-500 text-white' 
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              <Bookmark className="w-5 h-5" />
              İşaretle
            </button>
            
            <ErrorReportButton 
              questionId={currentQuestion.id}
              questionNumber={currentQuestion.question_number}
            />
          </div>
        </div>

        {/* Question Card */}
        <div ref={questionContainerRef} className="bg-white rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl scroll-mt-4">
          {/* Question ID Badge */}
          {currentQuestion.question_number && (
            <div className="mb-3 sm:mb-4">
              <span className="inline-block px-2 sm:px-3 py-1 bg-blue-100 text-blue-800 text-xs sm:text-sm font-mono rounded-full">
                ID: {currentQuestion.question_number}
              </span>
            </div>
          )}
          
          <h2 
            className="text-gray-800 text-xl font-bold mb-6 select-none" 
            style={{userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none'}}
            onContextMenu={(e) => e.preventDefault()}
          >
            {currentQuestion.question_text}
          </h2>

          {currentQuestion.image_url && (
            <div className="flex justify-center mb-6">
              <div className="relative bg-white/10 backdrop-blur-sm rounded-xl p-3 shadow-lg border border-white/20 inline-block max-w-lg">
                <ImageLightbox 
                  src={fixImageUrl(currentQuestion.image_url)}
                  alt="Question" 
                  className="rounded-lg max-h-56 w-auto object-contain"
                  containerClassName="relative"
                />
              </div>
            </div>
          )}

          <div 
            className="space-y-3 select-none" 
            style={{userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none'}}
            onContextMenu={(e) => e.preventDefault()}
          >
            {currentQuestion.choices?.map((choice) => {
              const isUserChoice = answered === choice.letter;
              const isCorrectChoice = choice.letter === currentQuestion.correct_answer;
              
              let choiceClass = 'bg-white border-2 border-gray-300 text-gray-800';
              let animationClass = '';
              
              if (showFeedback) {
                if (isCorrectChoice) {
                  choiceClass = 'bg-green-100 border-2 border-green-500 text-gray-800';
                  animationClass = 'animate-pulse';
                } else if (isUserChoice && !isCorrect) {
                  choiceClass = 'bg-red-100 border-2 border-red-500 text-gray-800';
                  animationClass = 'animate-shake';
                }
              }
              
              return (
                <button
                  key={choice.letter}
                  onClick={() => handleAnswer(choice.letter)}
                  disabled={answeredCurrentQuestion}
                  className={`w-full text-left p-4 rounded-xl font-medium transition-all duration-300 transform ${choiceClass} ${animationClass} ${
                    !answeredCurrentQuestion ? 'hover:bg-gray-50 hover:scale-102 cursor-pointer' : 'cursor-default'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-lg">{choice.letter}.</span>
                    <span className="flex-1">{choice.text}</span>
                    {showFeedback && isCorrectChoice && (
                      <Check className="w-6 h-6 text-green-600 animate-bounce" />
                    )}
                    {showFeedback && isUserChoice && !isCorrect && (
                      <XCircle className="w-6 h-6 text-red-600 animate-pulse" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Explanation Box - Show when answered - Protected */}
          {showFeedback && showAnswersImmediately && (
            <div 
              className={`mt-6 rounded-xl p-6 border-2 select-none ${
                isCorrect 
                  ? 'bg-green-100 border-green-500' 
                  : 'bg-red-100 border-red-500'
              }`}
              style={{userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none'}}
              onContextMenu={(e) => e.preventDefault()}
            >
              <div className="flex items-start gap-3">
                {isCorrect ? (
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                )}
                <div>
                  <h3 className={`font-bold text-lg mb-2 ${
                    isCorrect ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {isCorrect ? '✅ Doğru' : '❌ Yanlış'}
                  </h3>
                  <p className="text-gray-800" style={{ whiteSpace: 'pre-wrap' }}>
                    <span className="font-semibold">Açıklama:</span> {currentQuestion.explanation}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex flex-col gap-4 mt-8 pt-6 border-t border-gray-200">
            {/* Mark Question & Error Report Buttons */}
            <div className="flex justify-center gap-4">
              <button
                onClick={() => toggleMarkQuestion(currentQuestion.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
                  markedQuestions.has(currentQuestion.id)
                    ? 'bg-yellow-500 text-gray-900'
                    : 'bg-gray-200 hover:bg-yellow-100 text-gray-700'
                }`}
              >
                <Bookmark className={`w-5 h-5 ${markedQuestions.has(currentQuestion.id) ? 'fill-current' : ''}`} />
                {markedQuestions.has(currentQuestion.id) ? 'İşaretlendi ✓' : 'İşaretle'}
              </button>
              
              {/* Error Report Button */}
              <ErrorReportButton question={currentQuestion} />
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={prevQuestion}
                disabled={currentIndex === 0}
                className="flex items-center gap-2 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Önceki
              </button>

              <button
                onClick={() => setShowFinishConfirm(true)}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-all flex items-center gap-2"
              >
                🏁 Sınavı Bitir
              </button>

              <button
                onClick={nextQuestion}
                disabled={currentIndex === questions.length - 1}
                className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sonraki →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Finish Confirmation Modal */}
      {showFinishConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Sınavı Bitirmek İstiyor musunuz?</h3>
            <p className="text-gray-600 mb-2">
              {Object.keys(userAnswers).length} / {questions.length} soru cevaplandı
            </p>
            {markedQuestions.size > 0 && (
              <p className="text-yellow-600 mb-4">
                ⚠️ {markedQuestions.size} işaretli soru var
              </p>
            )}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowFinishConfirm(false)}
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-bold transition-all"
              >
                Devam Et
              </button>
              <button
                onClick={() => {
                  setShowFinishConfirm(false);
                  handleFinishQuiz();
                }}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-all"
              >
                Bitir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Question List Modal */}
      {showQuestionList && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-slate-900 rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900 border-b border-white/20 p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-2xl font-black text-white">📊 Genel Bakış</h2>
                  <p className="text-white/60 text-sm mt-1">
                    Quiz ilerlemesi ve işaretli sorularınız
                  </p>
                </div>
                <button
                  onClick={() => setShowQuestionList(false)}
                  className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-lg transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Statistics */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="text-2xl font-black text-white">{questions.length}</div>
                  <div className="text-white/60 text-xs">Toplam Soru</div>
                </div>
                <div className="bg-green-500/20 rounded-lg p-3">
                  <div className="text-2xl font-black text-green-400">{totalAnswered}</div>
                  <div className="text-white/60 text-xs">Cevaplanan</div>
                </div>
                <div className="bg-yellow-500/20 rounded-lg p-3">
                  <div className="text-2xl font-black text-yellow-400">{markedQuestions.size}</div>
                  <div className="text-white/60 text-xs">İşaretli</div>
                </div>
                <div className="bg-blue-500/20 rounded-lg p-3">
                  <div className="text-2xl font-black text-blue-400">{questions.length - totalAnswered}</div>
                  <div className="text-white/60 text-xs">Kalan</div>
                </div>
              </div>
            </div>

            <div className="p-6 grid grid-cols-10 gap-3">
              {questions.map((q, index) => {
                const answered = userAnswers[q.id];
                const marked = markedQuestions.has(q.id);
                
                return (
                  <button
                    key={q.id}
                    onClick={() => goToQuestion(index)}
                    className={`aspect-square rounded-xl font-bold text-lg transition-all flex items-center justify-center relative ${
                      index === currentIndex
                        ? 'bg-blue-500 text-white ring-4 ring-blue-300'
                        : answered
                        ? 'bg-green-500 text-white'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {index + 1}
                    {marked && (
                      <Bookmark className="w-4 h-4 absolute top-1 right-1 text-yellow-400 fill-yellow-400" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
